import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import MultiFilterOptions from './components/MultiFilterOptions';
import ClearFilters from 'components/ClearFilters';
import {
  INITIAL_VALUE,
  MultiFilterOptionsProps,
} from './components/MultiFilterOptions/MultiFilterOptions.component';
import useQueryFilter from 'hooks/useQueryFilter';
import { FilterByForm } from 'models/Filters';
import useQueryParams from 'hooks/useQueryParams';
import utils, { getPageParams, isValueEmpty } from 'utils';
import { PAGE_PARAM_KEYWORD } from 'data/global';

import './MultiFilterModal.styles.scss';

type MultiFilterModalProps = {
  className?: string;
  pageParam?: string | string[];
} & MultiFilterOptionsProps &
  ModalProps;

const MultiFilterModal: React.ForwardRefRenderFunction<
  ModalRef,
  MultiFilterModalProps
> = (props, ref) => {
  const { className, filters, pageParam = PAGE_PARAM_KEYWORD, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('multi-filter-modal', className);

  const { t } = useTranslation();

  const { activeFilter } = useQueryFilter();
  const { replaceMultipleQueryParams } = useQueryParams();

  const allFilters = useMemo(
    () => filters.flatMap(({ value }) => value),
    [filters],
  );

  const filterMap = useMemo(
    () => new Map(filters.map((filter) => [filter.value, filter])),
    [filters],
  );

  const hasActiveFilter = useMemo(
    () => allFilters.find((value) => activeFilter[value]),
    [activeFilter, allFilters],
  );

  const initialValues = useMemo(
    () => ({
      filters: hasActiveFilter
        ? filters
            .filter(({ value }) => activeFilter[value])
            .map(({ value, isMultiple, getInitialValue }) => ({
              name: value,
              value: getInitialValue
                ? getInitialValue?.(activeFilter)
                : isMultiple
                ? utils.mapParamsToArray(activeFilter[value])
                : activeFilter[value],
            }))
        : [INITIAL_VALUE],
    }),
    [activeFilter, filters, hasActiveFilter],
  );

  const onSubmit = useCallback(
    ({ filters }: FilterByForm) => {
      const filterParams = filters
        .filter(({ name, value }) => name && !isValueEmpty(value))
        .reduce((acc, { name, value }) => {
          const filter = filterMap.get(name);

          if (filter?.apply) return { ...acc, ...filter.apply(value) };

          return {
            ...acc,
            [name]: Array.isArray(value)
              ? utils.mapArrayToParams(
                  utils.multiSelectValueToStringArray(value as any),
                )
              : value,
          };
        }, {});

      const pageParams = getPageParams(pageParam);

      replaceMultipleQueryParams({
        ...filterParams,
        ...pageParams,
      });

      modal?.close();
    },
    [filterMap, modal, pageParam, replaceMultipleQueryParams],
  );

  if (!filters.length) return null;

  return (
    <Modal ref={modalRef} className={classes} title={t('filters')} {...rest}>
      <Form
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <MultiFilterOptions filters={filters} />

            <div className="actions">
              <ClearFilters
                filters={allFilters}
                customOnClick={() => modal?.close()}
              />

              <Button variant="link" type="button" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={invalid}>
                {t('apply')}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(MultiFilterModal);
